/**
 * Per-class hero roster.
 *
 * Each class levels independently: switching class doesn't carry your level over.
 * The active heroes live in `state.party` (1 or 2 of them); every other class's
 * progression is parked in `state.heroStash[classId]` until you switch back to it.
 * Equipment is per-hero (each hero owns its own `equipped` set); the loot bag
 * (`state.inventory`) and consumables stay shared.
 */

import { heroDerivedStats } from './combat.js';
import { emptyEquipped } from './gameState.js';

/** A brand-new level-1 hero for a class. */
export function createHero(classId) {
  return {
    classId,
    level: 1,
    xp: 0,
    hp: 1,
    maxHp: 1,
    alive: true,
    reviveAt: 0,
    lockedUntil: 0,
    skills: [],
    skillPoints: 0,
    activeSkillVisual: null, // chosen attack-visual skill id (else last-learned wins)
    equipped: emptyEquipped(),
  };
}

/** Ensure a hero loaded from an old shape has the per-hero fields we rely on. */
function normalizeHero(hero) {
  if (!hero.equipped) hero.equipped = emptyEquipped();
  if (hero.lockedUntil == null) hero.lockedUntil = 0;
  return hero;
}

/** Whether a hero is still serving its death lockout. */
function isLocked(hero) {
  return (hero?.lockedUntil ?? 0) > Date.now();
}

/** Full-heal a hero from its derived stats and clear any downed/locked flags. */
function reviveFull(hero, data) {
  const hs = heroDerivedStats(hero, data.classes, hero.equipped, data.skills);
  hero.maxHp = hs.maxHp;
  hero.hp = hs.maxHp;
  hero.alive = true;
  hero.reviveAt = 0;
  hero.lockedUntil = 0;
}

/**
 * Bring a hero into the party.
 * - Locked heroes enter STILL DOWNED (the 6h penalty is NOT bypassed by
 *   re-selecting them) — only maxHp is refreshed so the HUD bar reads right.
 * - Brand-new heroes (first time picked) start at full health.
 * - Returning heroes KEEP the HP they had when they were last benched: swapping
 *   characters must not act as a free heal. We only refresh maxHp (gear/skills
 *   may have changed) and clamp current HP into the new range.
 *
 * @param {object} hero
 * @param {object} data
 * @param {boolean} isFresh — true if this hero was just created (no prior run)
 */
function enterParty(hero, data, isFresh) {
  const hs = heroDerivedStats(hero, data.classes, hero.equipped, data.skills);
  hero.maxHp = hs.maxHp;

  if (isLocked(hero)) {
    hero.hp = 0;
    hero.alive = false;
    return;
  }
  if (isFresh) {
    reviveFull(hero, data);
    return;
  }
  // Returning hero: preserve current HP (clamped), don't top it up.
  hero.hp = Math.max(0, Math.min(hero.hp ?? hs.maxHp, hs.maxHp));
  hero.alive = hero.hp > 0;
  hero.reviveAt = 0;
}

/**
 * Make `classId` the active hero (single-hero convenience wrapper around
 * setParty). Preserves each class's own progression and equipment.
 *
 * @param {object} state
 * @param {string} classId
 * @param {{classes:object, skills?:object}} data
 * @returns {object} the now-active hero
 */
export function switchActiveClass(state, classId, data) {
  return setParty(state, [classId], data)[0];
}

/**
 * Set the active party to the given class ids (1 or 2). Heroes currently in the
 * party that aren't in the new selection are stashed (carrying their level, xp,
 * skills and equipment). Selected classes are restored from the stash or created
 * fresh, then full-healed on entry.
 *
 * @param {object} state
 * @param {string[]} classIds  one or two class ids
 * @param {{classes:object, skills?:object}} data
 * @returns {object[]} the now-active party
 */
export function setParty(state, classIds, data) {
  state.heroStash ??= {};

  // Stash everyone currently active (we'll pull back the ones still selected).
  for (const hero of (state.party ?? [])) {
    if (hero) state.heroStash[hero.classId] = normalizeHero(hero);
  }

  const party = [];
  for (const classId of classIds) {
    if (!classId || party.some((h) => h.classId === classId)) continue; // de-dupe
    const stashed = state.heroStash[classId];
    const hero = normalizeHero(stashed ?? createHero(classId));
    delete state.heroStash[classId];
    // Fresh heroes start full; returning heroes keep their HP (no free heal).
    enterParty(hero, data, !stashed); // locked heroes stay downed — no free revive
    party.push(hero);
  }

  state.party = party.length ? party : [normalizeHero(createHero('knight'))];
  return state.party;
}
